import { User } from "lucide-react";
import { cn } from "@/modern/utils";
import { swivuRecentRecordSortFn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import type { User as BoardUser } from "@/bryntum-lib/models/User";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { entityFinderRowClass } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import {
  canCreateUser,
  estimateDefaultRowHeight,
  openEntityEditor,
} from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  ENTITY_FINDER_SUBTITLE_CLS,
  ENTITY_FINDER_TITLE_CLS,
} from "@/modern/components/entity-finder/constants/layout";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";
import { UserAvatar, type BoardUser as AvatarUser } from "@/modern/components/user-avatar";

function mapUser(user: BoardUser) {
  return {
    id: `user-${user.id}`,
    type: "user" as const,
    label: user.name || "N/A",
    subtitle: user.email || undefined,
    record: user,
  };
}

export const userConfig: EntityFinderConfigEntry<"user"> = {
  type: "user",
  label: "Board User",
  plural: "Users",
  icon: User,
  tint: "bg-primary/15 text-primary",
  listOrder: 50,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "user"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.boardUsersStore.query(
        (boardUser: BoardUser) =>
          passesArchiveFilter(boardUser.archived, ctx.isShowArchive) &&
          !boardUser.isLinked &&
          (matchesSearch(boardUser.name, searchLower) || matchesSearch(boardUser.email, searchLower)),
        true
      ) as unknown as BoardUser[],
      {
        disableUpdatedBySort: true,
        getUpdatedAtField: (r) =>
          (r as BoardUser).lastInteractionHQ ?? (r as BoardUser).lastLogin ?? (r as BoardUser).createdAt,
      }
    );
    return records.map(mapUser);
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.boardUsersStore.query(
        (u: BoardUser) => passesArchiveFilter(u.archived, ctx.isShowArchive) && !u.isLinked,
        true
      ) as unknown as BoardUser[],
      {
        disableUpdatedBySort: true,
        getUpdatedAtField: (r) =>
          (r as BoardUser).lastInteractionHQ ?? (r as BoardUser).lastLogin ?? (r as BoardUser).createdAt,
      }
    ).slice(0, 1);
    return records[0] ? mapUser(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(userConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  itemRenderer: ({ item, query }) => (
    <div className={entityFinderRowClass(Boolean(item.subtitle))}>
      <div className={cn("shrink-0", item.subtitle && "mt-0.5")}>
        <UserAvatar
          user={item.record as unknown as AvatarUser}
          size="default"
          showTooltip={false}
          className="shrink-0"
          avatarClassName="size-8"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn("truncate", ENTITY_FINDER_TITLE_CLS)}>
          <EntityFinderHighlight text={item.label} query={query} />
        </div>
        {item.subtitle ? (
          <p className={cn("mt-0.5 truncate", ENTITY_FINDER_SUBTITLE_CLS)}>
            <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
          </p>
        ) : null}
      </div>
    </div>
  ),
  onSelect: (item, ctx) => {
    openEntityEditor(ctx, EDITOR_KEYS.userEditor, item.record, { user: item.record });
  },
  createOnUnmatched: {
    label: "Create new user",
    fn: (query, ctx) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const newRecord = (
        ctx.project.boardUsersStore as { createRecord: (data?: object) => unknown }
      ).createRecord({ name: trimmed });
      openEntityEditor(ctx, EDITOR_KEYS.userEditor, newRecord, { user: newRecord });
    },
    isEnabled: canCreateUser,
  },
};
